import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { SearchFilters, StoreData } from "./types";
import { fromSlug } from "./url-utils";

type UseSearchProductsArgs = {
  base: string;
  covaLocationId: number | null | undefined;
  filters: SearchFilters;
  hasMatchedSlugs: boolean;
  setHasMatchedSlugs: (v: boolean) => void;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
};

export function buildStoreProductsSearchParams(filters: SearchFilters) {
  const params = new URLSearchParams({
    page: String(filters.page),
    limit: String(filters.limit),
  });

  if (filters.q) params.set("q", filters.q);
  for (const category of filters.categories) params.append("category", category);
  params.set("grouped", "true");
  for (const brand of filters.brands) params.append("brand", brand);
  for (const collection of filters.collections) params.append("collection", collection);
  for (const subCategory of filters.subCategories) params.append("subCategory", subCategory);
  for (const size of filters.sizes) params.append("size", size);
  for (const growingMethod of filters.growingMethods) params.append("growingMethod", growingMethod);
  for (const type of filters.types) params.append("type", type);
  for (const strain of filters.strains) params.append("strain", strain);
  for (const dominantTerpene of filters.dominantTerpenes) params.append("dominantTerpene", dominantTerpene);
  for (const flavor of filters.flavors) params.append("flavor", flavor);
  for (const provinceOfOrigin of filters.provincesOfOrigin) params.append("provinceOfOrigin", provinceOfOrigin);
  for (const regionOfProduct of filters.regionsOfProduct) params.append("regionOfProduct", regionOfProduct);
  for (const extractionProcess of filters.extractionProcesses) params.append("extractionProcess", extractionProcess);
  for (const carrierOil of filters.carrierOils) params.append("carrierOil", carrierOil);
  if (filters.strainType) params.set("strainType", filters.strainType);
  if (filters.onSale) params.set("onSale", "true");
  if (filters.thcMin) params.set("thcMin", filters.thcMin);
  if (filters.thcMax) params.set("thcMax", filters.thcMax);
  if (filters.cbdMin) params.set("cbdMin", filters.cbdMin);
  if (filters.cbdMax) params.set("cbdMax", filters.cbdMax);
  if (filters.cbnMin) params.set("cbnMin", filters.cbnMin);
  if (filters.cbnMax) params.set("cbnMax", filters.cbnMax);
  if (filters.cbcMin) params.set("cbcMin", filters.cbcMin);
  if (filters.cbcMax) params.set("cbcMax", filters.cbcMax);
  if (filters.thcvMin) params.set("thcvMin", filters.thcvMin);
  if (filters.thcvMax) params.set("thcvMax", filters.thcvMax);
  if (filters.piecesMin) params.set("piecesMin", filters.piecesMin);
  if (filters.piecesMax) params.set("piecesMax", filters.piecesMax);
  if (filters.totalTerpeneMin) params.set("totalTerpeneMin", filters.totalTerpeneMin);
  if (filters.totalTerpeneMax) params.set("totalTerpeneMax", filters.totalTerpeneMax);
  if (filters.priceMin) params.set("priceMin", filters.priceMin);
  if (filters.priceMax) params.set("priceMax", filters.priceMax);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);

  return params;
}

export function matchSearchFilterSlugs(
  filters: SearchFilters,
  data: Pick<StoreData, "categories" | "brands" | "collections" | "strains" | "terpenes" | "facets">
): SearchFilters {
  const facets = data.facets;
  const mapList = (values: string[], options: string[] = []) =>
    values.map((slugOrValue) => fromSlug(slugOrValue, options)).filter(Boolean);

  const mapped = {
    categories: mapList(filters.categories, facets?.categories || data.categories || []),
    brands: mapList(filters.brands, facets?.brands || data.brands || []),
    collections: mapList(filters.collections, data.collections || []),
    subCategories: mapList(filters.subCategories, facets?.subCategories || []),
    sizes: mapList(filters.sizes, facets?.sizes || []),
    growingMethods: mapList(filters.growingMethods, facets?.growingMethods || []),
    types: mapList(filters.types, facets?.types || []),
    strains: mapList(filters.strains, facets?.strains || data.strains || []),
    dominantTerpenes: mapList(
      filters.dominantTerpenes,
      facets?.dominantTerpenes || data.terpenes || []
    ),
    flavors: mapList(filters.flavors, facets?.flavors || []),
    provincesOfOrigin: mapList(filters.provincesOfOrigin, facets?.provincesOfOrigin || []),
    regionsOfProduct: mapList(filters.regionsOfProduct, facets?.regionsOfProduct || []),
    extractionProcesses: mapList(filters.extractionProcesses, facets?.extractionProcesses || []),
    carrierOils: mapList(filters.carrierOils, facets?.carrierOils || []),
  };

  /* Bail out (return original ref) if every mapped list is element-equal
     to the source. Without this, a fresh object with identical values
     still triggers the fetch effect to re-run, which then re-enters
     this code path and produces an unbounded loop. */
  const sameArr = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const allSame = (Object.keys(mapped) as Array<keyof typeof mapped>).every(
    (k) => sameArr(filters[k] as string[], mapped[k])
  );
  if (allSame) return filters;

  return { ...filters, ...mapped };
}

export function useSearchProducts({
  base,
  covaLocationId,
  filters,
  hasMatchedSlugs,
  setHasMatchedSlugs,
  setFilters,
}: UseSearchProductsArgs) {
  const [data, setData] = useState<StoreData | null>(null);
  const [loading, setLoading] = useState(true);
  /* Budget per attempt. 3.5s was tight enough that a normal mobile connection
     hitting the API origin (no CDN in front of it) would abort a request the
     server had already answered in well under a second, and the user got
     "Search timed out" on both /search and every /collections/... page, which
     reuses this hook. One retry costs nothing on the happy path and turns a
     single dropped packet into a slight delay instead of an error screen. */
  const REQUEST_TIMEOUT_MS = 10000;
  const MAX_ATTEMPTS = 2;
  /* Long enough to ride out a brief network stall, short enough that a genuinely
     offline user still gets an answer quickly. */
  const RETRY_DELAY_MS = 400;

  /* Stable serialization of filters. The hook callers update filters via
     setState, which creates a new object reference every time even when
     values are identical. Depending on the object directly causes the
     fetch effect to re-run on every render path that touches setFilters
     , including the effect's own slug-matching write-back, and React
     escalates that to "Maximum update depth exceeded". By collapsing
     filters into a JSON string, the dep stays stable across renders
     that don't actually change filter values. */
  const filtersKey = useMemo(
    () => JSON.stringify(buildStoreProductsSearchParams(filters).toString()),
    [filters]
  );

  useEffect(() => {
    if (!covaLocationId || !Number.isFinite(covaLocationId)) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Distinguish an unmount/dependency-change abort from a timeout. Without
    // this, a timeout left `loading` stuck true forever (the finally skipped
    // setLoading whenever the signal was aborted, and nothing retried).
    let cancelled = false;
    // Reassigned per attempt so the cleanup below always aborts the live one.
    let activeController: AbortController | null = null;
    let activeTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));

    (async () => {
      let lastFailureWasTimeout = false;
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt++) {
        const controller = new AbortController();
        activeController = controller;
        let didTimeout = false;
        const timeoutId = setTimeout(() => {
          didTimeout = true;
          controller.abort();
        }, REQUEST_TIMEOUT_MS);
        activeTimeoutId = timeoutId;

        try {
          const params = buildStoreProductsSearchParams(filters);
          const res = await fetch(
            `${base}/api/store/${covaLocationId}/products?${params}`,
            { cache: "no-store", signal: controller.signal }
          );
          const json = (await res.json()) as StoreData;
          if (cancelled) return;
          setData(json);
          if (!hasMatchedSlugs && json?.success) {
            setFilters((prev) => matchSearchFilterSlugs(prev, json));
            setHasMatchedSlugs(true);
          }
          setLoading(false);
          return; // success
        } catch (e: unknown) {
          // An abort caused by unmount or a filter change is not a failure and
          // must not be retried, the newer effect run owns the request now.
          if (cancelled) return;
          lastError = e;
          lastFailureWasTimeout = didTimeout;
          if (attempt < MAX_ATTEMPTS) await sleep(RETRY_DELAY_MS);
        } finally {
          clearTimeout(timeoutId);
        }
      }

      if (cancelled) return;

      // Every attempt failed: drop the skeleton and say so, rather than hanging.
      const message = lastFailureWasTimeout
        ? "Search timed out. Check your connection and try again."
        : lastError instanceof Error && lastError.name !== "AbortError"
          ? lastError.message || "Failed to load products"
          : "Failed to load products";
      setData({
        success: false,
        items: [],
        total: 0,
        totalPages: 0,
        categories: [],
        brands: [],
        strains: [],
        terpenes: [],
        collections: [],
        priceMap: {},
        facets: {},
        error: message,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      if (activeTimeoutId !== null) clearTimeout(activeTimeoutId);
      activeController?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filters is read via closure; filtersKey drives re-runs
  }, [
    covaLocationId,
    filtersKey,
    hasMatchedSlugs,
    setFilters,
    setHasMatchedSlugs,
    base,
  ]);

  return { data, loading, setData, setLoading };
}
